import { MapPin, Briefcase, Users, Zap, User } from "lucide-react";
import type { LinkedInJob, TechManager, JobStatus } from "../api/LinkedInJobsApi";
import { STATUS_OPTIONS } from "../api/LinkedInJobsApi";
import { daysAgo, formatPostedDate, EMPLOYMENT_COLORS } from "../utils";
import CompanyApplyChip from "shared/CompanyApplyChip";
import RemoteScopeChip from "shared/RemoteScopeChip";
import ColdFusionChip from "shared/ColdFusionChip";
import StatusNotePopover from "shared/StatusNotePopover";
import StatusChangePopover from "shared/StatusChangePopover";
import ReassignPopover from "shared/ReassignPopover";

export const STATUS_CHIP_SOLID: Record<string, { label: string; className: string }> = {
  new:            { label: "New",           className: "bg-blue-500 text-white border-blue-400" },
  reviewing:      { label: "Reviewing",     className: "bg-violet-500 text-white border-violet-400" },
  applied:        { label: "Applied",       className: "bg-sky-500 text-sky-950 border-sky-400" },
  interviewing:   { label: "Interviewing",  className: "bg-amber-500 text-amber-950 border-amber-400" },
  offer_received: { label: "Offer Received", className: "bg-teal-500 text-white border-teal-400" },
  hired:          { label: "Hired",         className: "bg-emerald-500 text-emerald-950 border-emerald-400" },
  rejected:       { label: "Rejected",      className: "bg-red-500 text-white border-red-400" },
  not_relevant:   { label: "Not Relevant",  className: "bg-zinc-500 text-white border-zinc-400" },
  archived:       { label: "Archived",      className: "bg-zinc-700 text-zinc-200 border-zinc-600" },
};

interface Props {
  job: LinkedInJob;
  onClick: () => void;
  // Only passed by the caller when the admin is viewing "All TMs" (no manager
  // filter selected) — otherwise the assignee is already implied by the filter.
  assignedName?: string | null;
  // Present only for admins — candidate list + handler for the reassign popover.
  techManagers?: TechManager[];
  onReassign?: (alertId: number, employeeId: number) => Promise<void>;
  // Lets the status chip change status directly from the card, without opening the details modal.
  onStatusChange?: (alertId: number, status: JobStatus, notes: string | null) => Promise<void>;
  // Slimmer layout for the Kanban board, where columns are narrow and already
  // convey status by grouping — drops the status chip, job function, seniority
  // badge, easy-apply badge, and applicant count in favor of fewer, tighter rows.
  compact?: boolean;
}

export default function JobCard({ job, onClick, assignedName, techManagers, onReassign, onStatusChange, compact }: Props) {
  const employmentColor = EMPLOYMENT_COLORS[job.job_employment_type ?? ""] ?? "bg-muted/50 text-muted-foreground border-border/40";
  const statusEntry = job.status ? STATUS_CHIP_SOLID[job.status] : null;

  if (compact) {
    return (
      <div
        onClick={onClick}
        className={`bg-surface/60 border rounded-xl p-3 cursor-pointer hover:border-border transition-all select-none ${
          job.is_active ? "border-border/60" : "border-red-900/40 opacity-60"
        }`}
      >
        <div className="flex items-start gap-2.5">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className="w-8 h-8 rounded-lg border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
              {job.company_logo ? (
                <img
                  src={job.company_logo}
                  alt={job.company_name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                  }}
                />
              ) : null}
              <span className={`text-xs font-bold text-primary font-inter ${job.company_logo ? "hidden" : ""}`}>
                {job.company_name.charAt(0).toUpperCase()}
              </span>
            </div>
            {job.job_posted_date && (
              <span
                title={formatPostedDate(job.job_posted_date)}
                className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse"
              >
                {daysAgo(job.job_posted_date)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {job.job_title}
            </h3>
            <div className="flex items-center gap-1 mt-0.5 min-w-0">
              <span className="text-[11px] text-muted-foreground font-inter truncate">{job.company_name}</span>
              {job.job_location && (
                <>
                  <span className="text-muted-foreground text-[11px] shrink-0">•</span>
                  <span className="text-[11px] text-muted-foreground font-inter truncate">{job.job_location}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {(job.company_can_apply || job.is_coldfusion || !job.is_active) && (
          <div className="flex flex-wrap items-center gap-1 mt-2">
            <CompanyApplyChip companyCanApply={job.company_can_apply} metadataJson={job.company_analysis_metadata} />
            <ColdFusionChip show={job.is_coldfusion} />
            {!job.is_active && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                Deactivated
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5 mt-2">
          {job.job_employment_type && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium shrink-0 ${employmentColor}`}>
              {job.job_employment_type}
            </span>
          )}
          {job.status && (
            <RemoteScopeChip
              remoteScope={job.remote_scope}
              analysisJson={job.remote_worldwide_analysis}
              className="ml-auto"
            />
          )}
        </div>

        {assignedName && (
          <div className="flex items-center mt-2 pt-2 border-t border-border/40">
            {onReassign && job.alert_id != null ? (
              <ReassignPopover
                candidates={techManagers ?? []}
                currentEmployeeId={job.employee_id}
                onReassign={(employeeId) => onReassign(job.alert_id as number, employeeId)}
              >
                <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter">
                  <User className="w-2.5 h-2.5" />
                  {assignedName}
                </span>
              </ReassignPopover>
            ) : (
              <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter">
                <User className="w-2.5 h-2.5" />
                {assignedName}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`bg-surface/60 border rounded-xl p-4 cursor-pointer hover:border-border transition-all select-none ${
        job.is_active ? "border-border/60" : "border-red-900/40 opacity-60"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Company logo */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
            {job.company_logo ? (
              <img
                src={job.company_logo}
                alt={job.company_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                }}
              />
            ) : null}
            <span className={`text-sm font-bold text-primary font-inter ${job.company_logo ? "hidden" : ""}`}>
              {job.company_name.charAt(0).toUpperCase()}
            </span>
          </div>
          {job.job_posted_date && (
            <span
              title={formatPostedDate(job.job_posted_date)}
              className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse"
            >
              {daysAgo(job.job_posted_date)}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {job.job_title}
            </h3>
            <CompanyApplyChip companyCanApply={job.company_can_apply} metadataJson={job.company_analysis_metadata} />
            <ColdFusionChip show={job.is_coldfusion} />
            {statusEntry && (
              onStatusChange && job.alert_id != null ? (
                <StatusChangePopover
                  options={STATUS_OPTIONS}
                  statusChipMap={STATUS_CHIP_SOLID}
                  currentStatus={job.status as string}
                  note={job.alert_notes}
                  fromLabel={job.alert_from_status ? STATUS_CHIP_SOLID[job.alert_from_status]?.label : null}
                  onChange={(status, notes) => onStatusChange(job.alert_id as number, status as JobStatus, notes)}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                    {statusEntry.label}
                  </span>
                </StatusChangePopover>
              ) : (
                <StatusNotePopover
                  note={job.alert_notes}
                  fromLabel={job.alert_from_status ? STATUS_CHIP_SOLID[job.alert_from_status]?.label : null}
                  toLabel={statusEntry.label}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className} ${job.alert_notes ? "cursor-help" : ""}`}>
                    {statusEntry.label}
                  </span>
                </StatusNotePopover>
              )
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
            <span className="text-xs text-muted-foreground font-inter truncate">{job.company_name}</span>
            {job.job_location && (
              <>
                <span className="text-muted-foreground text-xs shrink-0">•</span>
                <span className="flex items-center gap-0.5 text-xs text-muted-foreground font-inter truncate shrink-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  {job.job_location}
                </span>
              </>
            )}
          </div>

          {/* Function */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
            {job.job_function && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter truncate max-w-[180px]">
                <Briefcase className="w-3 h-3 shrink-0" />
                {job.job_function.split(",")[0].trim()}
              </span>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {job.job_employment_type && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${employmentColor}`}>
                {job.job_employment_type}
              </span>
            )}
            {job.job_seniority_level && job.job_seniority_level !== "Not Applicable" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {job.job_seniority_level}
              </span>
            )}
            {job.is_easy_apply && (
              <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-inter">
                <Zap className="w-2.5 h-2.5" />
                Easy Apply
              </span>
            )}
            {job.job_num_applicants > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground font-inter">
                <Users className="w-2.5 h-2.5" />
                {job.job_num_applicants}
              </span>
            )}
            {!job.is_active && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                Deactivated
              </span>
            )}
            {/* Remote scope chip pinned to the right */}
            {job.status && (
              <RemoteScopeChip
                remoteScope={job.remote_scope}
                analysisJson={job.remote_worldwide_analysis}
                className="ml-auto"
              />
            )}
          </div>

          {assignedName && (
            <div className="flex items-center gap-1.5 mt-2">
              <div className="ml-auto">
                {onReassign && job.alert_id != null ? (
                  <ReassignPopover
                    candidates={techManagers ?? []}
                    currentEmployeeId={job.employee_id}
                    onReassign={(employeeId) => onReassign(job.alert_id as number, employeeId)}
                  >
                    <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter">
                      <User className="w-2.5 h-2.5" />
                      {assignedName}
                    </span>
                  </ReassignPopover>
                ) : (
                  <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter">
                    <User className="w-2.5 h-2.5" />
                    {assignedName}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
